/**
 * Minimap — a near top-down 3D camera onto the SR chart (NOT a 2D redraw), the
 * same logic as the Flow editor's minimap. A small R3F canvas with a high,
 * slightly-tilted perspective camera looking down the runway. The note world
 * scrolls each frame so the current beat sits at the HIT line, pinned to the
 * BOTTOM edge of the minimap (matching the highway's trigger zone). Notes render
 * as dots and rails as smooth Catmull-Rom curves — mirroring the 3D highway.
 *
 * Beat reference frame: the world scrolls by the OFFSET-ADJUSTED chart beat
 * ((currentTime - offset) / 60 * bpm) — the SAME frame the 3D highway scrolls on —
 * so a note (positioned at its offset-free time_beats) reaches the HIT line at the
 * exact moment it reaches the highway's trigger. (Scrolling by the RAW beat made
 * notes surface ~offset-seconds too EARLY relative to the 3D.) Clicking seeks back
 * through the offset so a clicked note still lands on the HIT line.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { Canvas, useFrame, useThree, type ThreeEvent } from '@react-three/fiber';
import { Line } from '@react-three/drei';
import * as THREE from 'three';
import { useEditorStore, getNoteColorsFromSettings } from '../../store/editorStore';
import { GRID_SCALE } from '../../types/beatmap';
import type { Note } from '../../types/beatmap';
import { accentHex, tintHex } from '../ui/themeColors';
import { useTheme } from '../ui/themeStore';
import { color, font } from '../ui/theme';
import { minimapWrapStyle } from '../ui/editorSharedStyles';
import { IcSettings } from '../ui/icons';
import GradientSlider from '../ui/GradientSlider';
const ZPB = 0.6;            // world Z per beat (ahead = -Z)
const TRACK_W = 5;         // world width of the play area (matches TUNNEL_WIDTH)
const FLOOR_LEN = 80;      // long static deck so it never runs out
const NOTE_X_SCALE = GRID_SCALE * 10; // same X mapping as the 3D highway
// Camera pose (fixed) — high, tilted down and forward, like Flow's default.
const CAM_HEIGHT = 7.4;
const CAM_POSZ = 1.7;
const CAM_TILT_DEG = 40;

/** Current OFFSET-ADJUSTED chart beat — the frame the 3D highway scrolls on, so a
 *  note (at its offset-free time_beats) sits at the HIT line exactly when it hits
 *  on the highway. Computed from currentTime just like the 3D's chartBeat(). */
function beatNow(): number {
  const s = useEditorStore.getState();
  const bpm = s.beatmap?.bpm || 120;
  const offsetSec = (s.beatmap?.offset || 0) / 1000;
  return ((s.playback.currentTime - offsetSec) / 60) * bpm;
}

/** Applies the tilted camera pose from the (user-adjustable) minimap camera. */
function MinimapCameraRig() {
  const { camera } = useThree();
  const cam = useEditorStore(s => s.minimapCam);
  useEffect(() => {
    const D = 8;
    const pitch = THREE.MathUtils.degToRad(cam.tilt);
    camera.position.set(0, cam.height, cam.posZ);
    camera.lookAt(0, cam.height - Math.sin(pitch) * D, cam.posZ - Math.cos(pitch) * D);
    camera.updateProjectionMatrix();
  }, [camera, cam.height, cam.posZ, cam.tilt]);
  return null;
}

/** A single rail rendered as a smooth Catmull-Rom curve (like the highway). */
function MinimapRail({ note, bpm, color, zpb }: { note: Note; bpm: number; color: string; zpb: number }) {
  const pts = useMemo(() => {
    if (!note.has_rail || !note.rail_nodes || note.rail_nodes.length < 1) return null;
    const raw: THREE.Vector3[] = [
      new THREE.Vector3(note.position[0] * NOTE_X_SCALE, 0.04, -note.time_beats * zpb),
    ];
    for (const node of note.rail_nodes) {
      const nodeBeats = ((node[2] / 0.02) / 1000) * (bpm / 60);
      raw.push(new THREE.Vector3(node[0] * NOTE_X_SCALE, 0.04, -nodeBeats * zpb));
    }
    if (raw.length < 2) return null;
    const curve = new THREE.CatmullRomCurve3(raw, false, 'catmullrom', 0.5);
    return curve.getPoints(Math.max(12, raw.length * 8)).map(p => [p.x, p.y, p.z] as [number, number, number]);
  }, [note, bpm, zpb]);
  if (!pts) return null;
  return <Line points={pts} color={color} lineWidth={3} transparent opacity={0.7} />;
}

// How far around the playhead the minimap actually shows (beats). Items outside
// never reach the GPU — the old code mounted the ENTIRE chart (a mesh per note,
// ~200 draw calls/frame on dense maps in a 163px-wide panel).
const WIN_BEHIND = 12;
const WIN_AHEAD = 130;
/** Instance pool caps — one draw call each regardless of chart density. */
const NOTE_CAP = 512;
const SLIDE_CAP = 256;

/** All note dots as ONE InstancedMesh + all slide bars as ONE InstancedMesh,
 *  windowed to [beat-WIN_BEHIND, beat+WIN_AHEAD] imperatively each frame (no
 *  React re-renders while scrolling). */
function InstancedMinimapMarkers({ notes, slides, noteColors, zpb }: {
  notes: Note[];
  slides: { time_beats: number }[];
  noteColors: Record<string, string>;
  /** World-Z per beat (ZPB × the user's Zoom). */
  zpb: number;
}) {
  const noteMeshRef = useRef<THREE.InstancedMesh>(null);
  const slideMeshRef = useRef<THREE.InstancedMesh>(null);

  // Sorted positions (the source arrays are already time-ordered; sort anyway —
  // cheap, and the binary search below depends on it).
  const noteData = useMemo(() =>
    notes
      .map(n => ({ x: n.position[0] * NOTE_X_SCALE, beat: n.time_beats, color: new THREE.Color(noteColors[n.note_type] || '#ffffff') }))
      .sort((a, b) => a.beat - b.beat),
    [notes, noteColors]);
  const slideBeats = useMemo(() => slides.map(s => s.time_beats).sort((a, b) => a - b), [slides]);

  const m4 = useMemo(() => new THREE.Matrix4(), []);
  const lastWin = useRef('');

  useFrame(() => {
    const beat = beatNow();
    // The window is a fixed stretch of RUNWAY, so zooming out (smaller zpb)
    // fits more beats on screen and the window widens to match.
    const scale = ZPB / zpb;
    const lo = beat - WIN_BEHIND * scale, hi = beat + WIN_AHEAD * scale;
    // Skip the rewrite entirely unless the window moved by ≥ half a beat.
    const winKey = `${Math.round(lo * 2)}`;
    if (winKey === lastWin.current) return;
    lastWin.current = winKey;

    const nm = noteMeshRef.current;
    if (nm) {
      let lo2 = 0, hi2 = noteData.length;
      while (lo2 < hi2) { const m = (lo2 + hi2) >>> 1; if (noteData[m].beat < lo) lo2 = m + 1; else hi2 = m; }
      let w = 0;
      for (let i = lo2; i < noteData.length && noteData[i].beat <= hi && w < NOTE_CAP; i++, w++) {
        const d = noteData[i];
        m4.makeTranslation(d.x, 0.06, -d.beat * zpb);
        nm.setMatrixAt(w, m4);
        nm.setColorAt(w, d.color);
      }
      nm.count = w;
      nm.instanceMatrix.needsUpdate = true;
      if (nm.instanceColor) nm.instanceColor.needsUpdate = true;
    }
    const sm = slideMeshRef.current;
    if (sm) {
      let lo2 = 0, hi2 = slideBeats.length;
      while (lo2 < hi2) { const m = (lo2 + hi2) >>> 1; if (slideBeats[m] < lo) lo2 = m + 1; else hi2 = m; }
      let w = 0;
      for (let i = lo2; i < slideBeats.length && slideBeats[i] <= hi && w < SLIDE_CAP; i++, w++) {
        // Lay the bar flat (the old per-slide planes were rotated -π/2 on X).
        m4.makeRotationX(-Math.PI / 2);
        m4.setPosition(0, 0.03, -slideBeats[i] * zpb);
        sm.setMatrixAt(w, m4);
      }
      sm.count = w;
      sm.instanceMatrix.needsUpdate = true;
    }
  });

  // Force a window rewrite when the chart data (or the zoom) changes under the
  // same beat.
  useEffect(() => { lastWin.current = ''; }, [noteData, slideBeats, zpb]);

  return (
    <>
      <instancedMesh ref={noteMeshRef} args={[undefined, undefined, NOTE_CAP]} frustumCulled={false}>
        {/* Low-poly dot — it's ~4px on screen; the old per-note spheres were 10×8. */}
        <sphereGeometry args={[0.12, 6, 5]} />
        <meshBasicMaterial />
      </instancedMesh>
      <instancedMesh ref={slideMeshRef} args={[undefined, undefined, SLIDE_CAP]} frustumCulled={false}>
        <planeGeometry args={[TRACK_W, 0.08]} />
        <meshBasicMaterial color="#a855f7" transparent opacity={0.5} side={THREE.DoubleSide} />
      </instancedMesh>
    </>
  );
}

/** Scene contents — scrolls the note world so the playhead sits at z=0 (HIT). */
function MinimapScene({ deck, hit, divider }: { deck: string; hit: string; divider: string }) {
  const beatmap = useEditorStore(s => s.beatmap);
  const activeDifficulty = useEditorStore(s => s.activeDifficulty);
  const gp = useEditorStore(s => s.gameplaySettings);
  const worldRef = useRef<THREE.Group>(null);

  const bpm = beatmap?.bpm || 120;
  const diff = beatmap?.difficulties[activeDifficulty];
  // Beat spacing — the Zoom slider in the minimap options (⚙ in the header).
  const zpb = ZPB * (useEditorStore(s => s.minimapZoom) || 1);
  const noteColors = useMemo(
    () => getNoteColorsFromSettings(gp),
    [gp.noteColorLeft, gp.noteColorRight, gp.noteColorOneHand, gp.noteColorBothHands], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Rails still render as individual smooth curves (they're few), but only the
  // ones overlapping the visible window — refreshed every 4 beats via a coarse
  // bucket so scrolling doesn't re-render React every frame.
  const [beatBucket, setBeatBucket] = React.useState(0);
  useFrame(() => {
    if (worldRef.current) worldRef.current.position.z = beatNow() * zpb;
    const b = Math.floor(beatNow() / 4);
    setBeatBucket(prev => (prev === b ? prev : b));
  });
  const visibleRails = useMemo(() => {
    if (!diff) return [];
    const beat = beatBucket * 4;
    const scale = ZPB / zpb;
    const lo = beat - WIN_BEHIND * scale - 8, hi = beat + WIN_AHEAD * scale + 8;
    return diff.notes.filter(n => {
      if (!n.has_rail || !n.rail_nodes || n.rail_nodes.length < 1) return false;
      const last = n.rail_nodes[n.rail_nodes.length - 1];
      const endBeats = ((last[2] / 0.02) / 1000) * (bpm / 60);
      return n.time_beats <= hi && endBeats >= lo;
    });
  }, [diff, beatBucket, bpm, zpb]);

  const LANES = 6;
  const laneW = TRACK_W / LANES;

  return (
    <>
      <ambientLight intensity={1.5} />

      {/* Static deck + lane dividers (camera-fixed) */}
      <mesh position={[0, -0.01, -FLOOR_LEN / 2 + 4]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[TRACK_W, FLOOR_LEN]} />
        <meshBasicMaterial color={deck} />
      </mesh>
      {Array.from({ length: LANES + 1 }).map((_, i) => (
        <mesh key={i} position={[-TRACK_W / 2 + laneW * i, 0.005, -FLOOR_LEN / 2 + 4]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[0.02, FLOOR_LEN]} />
          <meshBasicMaterial color={divider} transparent opacity={0.5} />
        </mesh>
      ))}

      {/* HIT line at z=0 (pinned to the bottom of the viewport) */}
      <mesh position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[TRACK_W + 0.3, 0.06]} />
        <meshBasicMaterial color={hit} />
      </mesh>

      {/* Notes + slides as two instanced pools (windowed); rails as curves. */}
      <group ref={worldRef}>
        <InstancedMinimapMarkers
          notes={diff?.notes ?? []}
          slides={diff?.slides ?? []}
          noteColors={noteColors}
          zpb={zpb}
        />
        {visibleRails.map((n, i) => (
          <MinimapRail key={`${n.time_ms}-${i}`} note={n} bpm={bpm} color={noteColors[n.note_type] || '#ffffff'} zpb={zpb} />
        ))}
      </group>
    </>
  );
}

/** ⚙ for the Minimap panel header — opens/closes the options block below the
 *  map (camera pose + zoom). Mirrors the Flow editor's minimap gear so the two
 *  editors' minimaps are operated the same way. */
export function MinimapOptionsButton() {
  const camOpen = useEditorStore(s => s.minimapCamOpen);
  const setCamOpen = useEditorStore(s => s.setMinimapCamOpen);
  return (
    <button
      type="button"
      aria-label="Minimap options"
      title="Minimap options — camera height, position, rotation and zoom"
      onClick={() => setCamOpen(!camOpen)}
      style={{ ...gearBtnStyle, ...(camOpen ? gearBtnActiveStyle : null) }}
    >
      <IcSettings size={13} />
    </button>
  );
}

export default function Minimap() {
  // Re-render (recolour) when the theme changes.
  const themeAccent = useTheme(s => s.accent);
  const themeTint = useTheme(s => s.tint);
  const cam = useEditorStore(s => s.minimapCam);
  const setCam = useEditorStore(s => s.setMinimapCam);
  const camOpen = useEditorStore(s => s.minimapCamOpen);
  const zoom = useEditorStore(s => s.minimapZoom);
  const setZoom = useEditorStore(s => s.setMinimapZoom);
  const cols = useMemo(() => ({
    deck: tintHex(0.12, 0.022),
    divider: tintHex(0.45, 0.06),
    hit: accentHex(0.70, 0.205),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [themeAccent, themeTint]);

  const handleClick = (e: ThreeEvent<MouseEvent>) => {
    const s = useEditorStore.getState();
    const bpm = s.beatmap?.bpm || 120;
    const offsetSec = (s.beatmap?.offset || 0) / 1000;
    // Click → beat uses the SAME spacing the scene is drawn at (Zoom included).
    const targetBeat = Math.max(0, beatNow() - e.point.z / (ZPB * (s.minimapZoom || 1)));
    // Seek: the audio time for a chart beat is beat*60/bpm PLUS the offset.
    s.setCurrentTime((targetBeat / bpm) * 60 + offsetSec);
  };

  // The titled container is provided by the CollapsibleBox wrapper in the
  // sidebar, so render just the minimap content here.
  return (
    <>
      <div style={minimapWrapStyle}>
        <Canvas
          camera={{ position: [0, CAM_HEIGHT, CAM_POSZ], fov: 42, near: 0.1, far: 100 }}
          gl={{ antialias: true }}
          // The panel is ~163 CSS px wide — rendering it at dpr 2 doubles the
          // fill for dots nobody can tell apart. 1.5 max keeps it crisp.
          dpr={[1, 1.5]}
          style={{ display: 'block', width: '100%', height: '100%' }}
        >
          <MinimapCameraRig />
          <MinimapScene deck={cols.deck} hit={cols.hit} divider={cols.divider} />
          {/* Invisible click-catcher floor spanning the visible track. */}
          <mesh position={[0, 0, -10]} rotation={[-Math.PI / 2, 0, 0]} onClick={handleClick}>
            <planeGeometry args={[TRACK_W + 1, 60]} />
            <meshBasicMaterial transparent opacity={0} depthWrite={false} />
          </mesh>
        </Canvas>
      </div>

      {/* Options (opened by the ⚙ in the panel header — see
          MinimapOptionsButton): camera pose + zoom, same set as Flow's
          "Minimap Options" window. */}
      {camOpen && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 6 }}>
          <GradientSlider inline label="Height" valueText={cam.height.toFixed(1)}
            value={(cam.height - 2) / 12}
            onChange={v => setCam({ height: Math.round((2 + v * 12) * 10) / 10 })} />
          <GradientSlider inline label="Pos" valueText={cam.posZ.toFixed(1)}
            value={(cam.posZ + 4) / 12}
            onChange={v => setCam({ posZ: Math.round((-4 + v * 12) * 10) / 10 })} />
          <GradientSlider inline label="Tilt" valueText={cam.tilt.toFixed(0)}
            value={(cam.tilt - 10) / 80}
            onChange={v => setCam({ tilt: Math.round(10 + v * 80) })} />
          {/* Zoom = beat spacing, so packed sections stop reading as a solid bar.
              Range 0.5–6× (was 2× — reviewers needed more spread). */}
          <GradientSlider inline label="Zoom" valueText={`${zoom.toFixed(2)}×`}
            value={(zoom - 0.5) / 5.5}
            onChange={v => setZoom(Math.round((0.5 + v * 5.5) * 20) / 20)} />
        </div>
      )}
    </>
  );
}

const gearBtnStyle: React.CSSProperties = {
  // Matches the Flow editor's minimap gear (minimapGearBtnStyle).
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: 22, height: 22, padding: 0, borderRadius: 7, cursor: 'pointer',
  fontFamily: font.sans,
  background: 'oklch(0.20 calc(0.028 * var(--tint-c)) var(--tint-h) / 0.55)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.3)',
  color: color.accentSoft,
  transition: 'all 0.15s ease',
};
const gearBtnActiveStyle: React.CSSProperties = {
  background: 'oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.18)',
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)',
  color: 'var(--accent)',
};

